

// Probably need an ID gotten off the route, useParams mayhaps?

import { Box, FormControlLabel, Grid, Paper, styled, Switch, Typography, useTheme } from "@mui/material";
import { gridClasses, GridColDef } from "@mui/x-data-grid";
import { GRID_AGGREGATION_ROOT_FOOTER_ROW_ID, GridAggregationModel } from "@mui/x-data-grid-premium";
import { GetFieldLookupItem, useFormMetadata, useLookups } from "@ngt/forms";
import { DateTime } from "luxon";
import { ChangeEvent, FC, useContext, useMemo } from "react";
import { Link, Outlet, useParams } from "react-router-dom";
import { ApiEnabledStatus, FormDefinitions, LotSummary, Permissions } from "../api/dtos";
import { DetailsContent, DetailsHeader, DetailsPanel, GridColumn, GridData, GridFillerRow, GridLabel, GridRow } from "../components/DetailsPanel";
import GridActions from "../components/GridActions";
import SetLinks from "../components/layout/SetLinks";
import StageAttachments from "../components/StageAttachments";
import { StyledButton } from "../components/StyledButton";
import CollapsibleTablePremium from "../components/tables/CollapsibleTablePremium";
import TitleBox from "../components/TitleBox";
import { createCustomToolbarProps } from "../components/utility/CustomToolbarProps";
import { StyledLink } from "../components/utility/StyledLink";
import PermissionsContext from "../contexts/auth/PermissionsContext";
import ProjectContext from "../contexts/data/ProjectContext";
import { DefaultLotCreateLink, DefaultProjectCreateLink, DefaultStageCreateLink } from "../contexts/layouts/LinkContext";
import LotSummariesContext from "../contexts/LotSummariesContext";
import StageAttachmentsContext from "../contexts/StageAttachmentsContext";
import StageDetailsContext from "../contexts/StageDetailsContext";
import useApiActivityStatus from "../hooks/data/useApiActivityStatusByStageId";
import useAttachmentsByStageId from "../hooks/data/useAttachmentsByStageId";
import useLotSummariesByStageId from "../hooks/data/useLotSummariesByStageId";
import usePostApiStatusUpdate from "../hooks/data/usePostApiStatusUpdate";
import useStageDetailsByProjectIdStageId from "../hooks/data/useStageDetailsByProjectIdStageId";

const StyledPaper = styled(Paper) (({theme}) => ({
    marginBottom: theme.spacing(2)
}));

const useLotColumns = (includeSubstages?: boolean) => {

    const {data: permissions} = useContext(PermissionsContext);

    const metadata = useFormMetadata(FormDefinitions.Lot);

    const {data: lookups, loading: _lookupsLoading} = useLookups(metadata);

    const projectId = useParams().projectId;
    const stageId = useParams().stageId;

    const lotColumns = useMemo(() => {
        const columns: GridColDef<LotSummary>[] =  [
            {
                field: "lotNo",
                headerName: "Lot No.",
                width: 90,
                renderCell: (params) => {
                    return (
                        <StyledLink 
                            to={`/projects/${projectId}/stages/${stageId}/lots/${params.row.id}`}
                        >
                            {params.formattedValue}
                        </StyledLink>
                    )
                }
            },
            {
                field: "status",
                headerName: "Sales Status",
                valueGetter: (_value, lot) => GetFieldLookupItem(lookups, 'Status', lot.status)?.value,
                minWidth: 125,
                flex: 1
            },
            {
                field: "listPrice",
                headerName: "List Price",
                type: "number",
                headerAlign: "left",
                align: "left",
                minWidth: 100,
                flex: 1,
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                }
            },
            {
                field: "contractPrice",
                headerName: "Contract Price",
                type: "number",
                headerAlign: "left",
                align: "left",
                minWidth: 105,
                flex: 1,
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                }
            },
            {
                field: "contractCount",
                headerName: "Contract Count",
                type: "number",
                headerAlign: "left",
                align: "left",
                width: 110
            },
            {
                field: "contractIssueDate",
                headerName: "Contract Issue Date",
                type: "date",
                width: 110,
                valueFormatter: (_value, row) => {
                    return row.contractIssueDate ? DateTime.fromISO(row.contractIssueDate).toFormat("dd/MM/yyyy") : undefined;
                },
                valueGetter: (_value, row) => {
                    return row.contractIssueDate ? new Date(row.contractIssueDate.toString()) : undefined;
                }
            },
            {
                field: "contractExchangeDate",
                headerName: "Contract Exchange Date",
                type: "date",
                width: 120,
                valueFormatter: (_value, row) => {
                    return row.contractExchangeDate ? DateTime.fromISO(row.contractExchangeDate).toFormat("dd/MM/yyyy") : undefined;
                },
                valueGetter: (_value, row) => {
                    return row.contractExchangeDate ? new Date(row.contractExchangeDate.toString()) : undefined;
                }
            },
            {
                field: "rateAdjustments",
                headerName: "Rate Adjustments",
                type: "number",
                headerAlign: "left",
                align: "left",
                width: 135,
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                }
            },
            {
                field: "legalFees",
                headerName: "Legal Fees",
                type: "number",
                headerAlign: "left",
                align: "left",
                minWidth: 100,
                flex: 1,
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                }
            },
            {
                field: "commission",
                headerName: "Commission",
                type: "number",
                headerAlign: "left",
                align: "left",
                minWidth: 135,
                flex: 1,
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                }
            },
            {
                field: "agentName",
                headerName: "Agent",
                minWidth: 100,
                flex: 1
            },
            {
                field: "settlementDate",
                headerName: "Settlement Date",
                type: "date",
                width: 125,
                valueFormatter: (_value, row) => {
                    return row.settlementDate ? DateTime.fromISO(row.settlementDate).toFormat("dd/MM/yyyy") : undefined;
                },
                valueGetter: (_value, row) => {
                    return row.settlementDate ? new Date(row.settlementDate.toString()) : undefined;
                }
            },
            {
                field: "settlementProceeds",
                headerName: "Settlement Proceeds",
                type: "number",
                headerAlign: "left",
                align: "left",
                minWidth: 125,
                flex: 1,
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                }
            }            
        ];

        const aggregationModel: GridAggregationModel = {
            listPrice: 'sum',
            contractPrice: 'sum',
            contractCount: 'sum',
            rateAdjustments: 'sum',
            legalFees: 'sum',
            commission: 'sum',
            settlementProceeds: 'sum'
        }

        if(includeSubstages) {
            columns.push({
                field: "subStage",
                headerName: "",
                width: 30,
                valueFormatter: (_value, row) => GetFieldLookupItem(lookups, 'SubStage', row.subStage)?.value,
                aggregable: false
            })
        }

        if(permissions?.includes(Permissions.StageCanEditLot) || permissions?.includes(Permissions.StageCanDeleteLot)) {
            columns.push({
                field: "actions",
                headerName: '',
                width: 90,
                renderCell: (params) => {
                    return (
                        params.id === GRID_AGGREGATION_ROOT_FOOTER_ROW_ID ? 
                        null :
                        <GridActions
                            editPermission={Permissions.StageCanEditLot}
                            deletePermission={Permissions.StageCanDeleteLot}
                            editUrl={`/projects/${projectId}/stages/${stageId}/lots/edit/${params.row.id}`}
                            deleteUrl={`/projects/${projectId}/stages/${stageId}/lots/delete/${params.row.id}`}
                        />
                    )
                },
                filterable: false,
                sortable: false,
                disableExport: true,
                aggregable: false
            });
        }

        return {columns: columns, aggregationModel: aggregationModel};
    }, [lookups, permissions, includeSubstages]);

    return lotColumns;
}

export const StageDetails: FC = () => {
    const theme = useTheme();

    const {data: permissions} = useContext(PermissionsContext);
    
    const params = useParams();

    const projectId = Number(params.projectId);
    const stageId = Number(params.stageId);

    const {data: project} = useContext(ProjectContext);

    const {data: stageApiStatus, refresh: refreshActivityStatus} = useApiActivityStatus(stageId, FormDefinitions.Stage);

    const stageAttachmentsContextData = useAttachmentsByStageId(stageId);

    const stageDetailsContextData = useStageDetailsByProjectIdStageId(projectId, stageId);
    const {data: stageData, loading: _stageDataLoading} = stageDetailsContextData;
    if(stageData?.stage && !stageData.stage.name) {
        stageData.stage.name = "Unnamed Stage";
    }

    const customToolbarProps = useMemo(() => createCustomToolbarProps({
        fileName: `${project?.name} - ${stageData?.stage?.name}`,
        exportTitle: `${project?.name} - ${stageData?.stage?.name}`,
        includeDateTime: true
    }), [project, stageData])

    const lotSummariesContextData = useLotSummariesByStageId(stageId);
    const {data: lotSummaries, loading: lotsLoading} = lotSummariesContextData;

    const {columns: columns, aggregationModel: aggregationModel} = useLotColumns(!!lotSummaries?.find(ls => ls.subStage != null));

    const updateApiStatus = usePostApiStatusUpdate();
    //const {data: lotSummaries, loading: lotsLoading} = useLotSummariesByStageId(stageId);

    const handleApiToggle = async (event: ChangeEvent<HTMLInputElement>) => {
        await updateApiStatus(stageId, FormDefinitions.Stage, event.target.checked);
        refreshActivityStatus();
    }
    
    return (

        //Properties

        //Summary

        //StageList
        <>
            <StageDetailsContext.Provider value={stageDetailsContextData}>
                <LotSummariesContext.Provider value={lotSummariesContextData}>
                    <StageAttachmentsContext.Provider value={stageAttachmentsContextData}>
                        <Outlet />

                        <SetLinks 
                            createProjectLink={DefaultProjectCreateLink} 
                            createStageLink={DefaultStageCreateLink}
                            createLotLink={DefaultLotCreateLink}
                        />

                        <TitleBox>
                            <Typography
                                variant="h2"
                                marginBottom={theme.spacing(2)}
                            >
                                {
                                    stageData?.projectName 
                                        ? `${stageData?.projectName} - ${stageData?.stage?.name}`
                                        : stageData?.stage?.name
                                }
                            </Typography>
                            {
                                permissions?.includes(Permissions.AdminAccess) &&
                                <Grid>
                                    <FormControlLabel
                                        label={
                                            "Enable Api" + (
                                                stageApiStatus == ApiEnabledStatus.Active 
                                                    ? ` - Id: ${stageId}`
                                                    : ""
                                            )
                                        }
                                        control={
                                            <Switch
                                                checked={stageApiStatus == ApiEnabledStatus.Active}
                                                disabled={stageApiStatus == ApiEnabledStatus.Unused}
                                                onChange={handleApiToggle}
                                            />
                                        }
                                    />
                                </Grid>
                            }
                            <Grid
                                container
                                gap={theme.spacing(2)}
                                justifyContent={"end"}
                            >
                                {
                                    permissions?.includes(Permissions.ProjectCanEditStage) &&
                                    <StyledButton 
                                            variant="contained"
                                            //@ts-expect-error
                                            component={Link}
                                            to={`/projects/${projectId}/stages/${stageId}/edit`}
                                            state={{
                                                back: true
                                            }}
                                    >
                                        Edit Stage
                                    </StyledButton>
                                }
                                {
                                    permissions?.includes(Permissions.StageCanAddLot) &&
                                    <StyledButton 
                                            variant="contained"
                                            //@ts-expect-error
                                            component={Link}
                                            to={`/projects/${projectId}/stages/${stageId}/lots/create`}
                                            state={{
                                                back: true
                                            }}
                                    >
                                        Add Lot
                                    </StyledButton>
                                }
                                {
                                    permissions?.includes(Permissions.StageCanAddAttachment) &&
                                    <StyledButton 
                                            variant="contained"
                                            //@ts-expect-error
                                            component={Link}
                                            to={`/projects/${projectId}/stages/${stageId}/attachments/create`}
                                            state={{
                                                back: true
                                            }}
                                    >
                                        Add Attachment
                                    </StyledButton>
                                }
                                {
                                    permissions?.includes(Permissions.StageCanStylePricelist) &&
                                    <StyledButton 
                                            variant="contained"
                                            //@ts-expect-error
                                            component={Link}
                                            to={`/projects/${projectId}/stages/${stageId}/style-pricelist`}
                                            state={{
                                                back: true
                                            }}
                                    >
                                        Style Pricelist
                                    </StyledButton>
                                }
                                {
                                    permissions?.includes(Permissions.StageCanCreatePricelist) &&
                                    <StyledButton 
                                            variant="contained"
                                            //@ts-expect-error
                                            component={Link}
                                            to={`/projects/${projectId}/stages/${stageId}/download-pricelist`}
                                            state={{
                                                back: true
                                            }}
                                    >
                                        Download Pricelist
                                    </StyledButton>
                                }
                                {
                                    permissions?.includes(Permissions.StageCanEditStagePlan) &&
                                    <StyledButton 
                                            variant="contained"
                                            //@ts-expect-error
                                            component={Link}
                                            to={`/projects/${projectId}/stages/${stageId}/stage-plan/edit`}
                                            state={{
                                                back: true
                                            }}
                                    >
                                        Edit Stage Plan
                                    </StyledButton>
                                } 
                                {
                                    permissions?.includes(Permissions.StageCanEditStagePlan) &&
                                    <StyledButton 
                                            variant="contained"
                                            //@ts-expect-error
                                            component={Link}
                                            to={`/projects/${projectId}/stages/${stageId}/stage-plan`}
                                            state={{
                                                back: true
                                            }}
                                    >
                                        Preview Stage Plan
                                    </StyledButton>
                                } 
                                {
                                    permissions?.includes(Permissions.StageCanBulkEditLotProperties) &&
                                    <StyledButton 
                                            variant="contained"
                                            //@ts-expect-error
                                            component={Link}
                                            to={`/projects/${projectId}/stages/${stageId}/bulk-edit-lot-properties`}
                                            state={{
                                                back: true
                                            }}
                                    >
                                        Bulk Edit Lot Properties
                                    </StyledButton>
                                }
                                {
                                    permissions?.includes(Permissions.StageCanBulkEditLots) &&
                                    <StyledButton 
                                            variant="contained"
                                            //@ts-expect-error
                                            component={Link}
                                            to={`/projects/${projectId}/stages/${stageId}/bulk-edit-lots`}
                                            state={{
                                                back: true
                                            }}
                                    >
                                        Bulk Edit Lots
                                    </StyledButton>
                                }
                                {
                                    permissions?.includes(Permissions.StageCanBulkEditContracts) &&
                                    <StyledButton 
                                            variant="contained"
                                            //@ts-expect-error
                                            component={Link}
                                            to={`/projects/${projectId}/stages/${stageId}/bulk-edit-contracts`}
                                            state={{
                                                back: true
                                            }}
                                    >
                                        Bulk Edit Contracts
                                    </StyledButton>
                                }
                            </Grid>
                        </TitleBox>
                        
                        <Grid
                            container
                            size={{
                                xs: 12
                            }}
                            spacing={{
                                xs: 2
                            }}
                        >
                            <Grid
                                size={{
                                    xs: 12,
                                    lg: 6
                                }}
                            >
                                <DetailsPanel>
                                    <DetailsHeader>
                                        Lot Summary
                                    </DetailsHeader>
                                    <DetailsContent>
                                        <GridColumn>
                                            <GridRow>
                                                <GridLabel>
                                                    Total Lots:
                                                </GridLabel>
                                                <GridData>
                                                    {stageData?.totalLots}
                                                </GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>
                                                    Coming Soon:
                                                </GridLabel>
                                                <GridData>
                                                    {stageData?.comingSoon}
                                                </GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>
                                                    On Hold:
                                                </GridLabel>
                                                <GridData>
                                                    {stageData?.onHold}
                                                </GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>
                                                    Available
                                                </GridLabel>
                                                <GridData>
                                                    {stageData?.available}
                                                </GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>
                                                    Sales Advice Issued:
                                                </GridLabel>
                                                <GridData>
                                                    {stageData?.salesAdviceIssued}
                                                </GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>
                                                    Contracts Issued:
                                                </GridLabel>
                                                <GridData>
                                                    {stageData?.contractsIssued}
                                                </GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>
                                                    Contracts Exchanged:
                                                </GridLabel>
                                                <GridData>
                                                    {stageData?.contractsExchanged}
                                                </GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>
                                                    Contracts Settled:
                                                </GridLabel>
                                                <GridData>
                                                    {stageData?.contractsSettled}
                                                </GridData>
                                            </GridRow>
                                            <GridFillerRow />
                                        </GridColumn>
                                    </DetailsContent>
                                </DetailsPanel>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    lg: 6
                                }}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'stretch'
                                }}
                            >
                                <DetailsPanel>
                                    <DetailsHeader>
                                        Financial Summary
                                    </DetailsHeader>
                                    <DetailsContent>
                                        <GridColumn>
                                            <GridRow>
                                                <GridLabel>
                                                    Total List Price: 
                                                </GridLabel>
                                                <GridData>
                                                    {
                                                        stageData?.totalListPrice ? Intl.NumberFormat("en-AU", {
                                                            style: "currency",
                                                            currency: "AUD"
                                                        }).format(stageData?.totalListPrice) : undefined
                                                    }
                                                </GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>
                                                    Average List Price: 
                                                </GridLabel>
                                                <GridData>
                                                    {
                                                        stageData?.averageListPrice ? Intl.NumberFormat("en-AU", {
                                                            style: "currency",
                                                            currency: "AUD"
                                                        }).format(stageData?.averageListPrice) : undefined
                                                    }
                                                </GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>
                                                    Total Settled Contract Price: 
                                                </GridLabel>
                                                <GridData>
                                                    {
                                                        stageData?.totalSettledContractPrice ? Intl.NumberFormat("en-AU", {
                                                            style: "currency",
                                                            currency: "AUD"
                                                        }).format(stageData?.totalSettledContractPrice) : undefined
                                                    }
                                                </GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>
                                                    Average Settled Contract Price: 
                                                </GridLabel>
                                                <GridData>
                                                    {
                                                        stageData?.averageSettledContractPrice ? Intl.NumberFormat("en-AU", {
                                                            style: "currency",
                                                            currency: "AUD"
                                                        }).format(stageData?.averageSettledContractPrice) : undefined
                                                    }
                                                </GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>
                                                    Total Unsettled Contract Price: 
                                                </GridLabel>
                                                <GridData>
                                                    {
                                                        stageData?.totalUnsettledContractPrice ? Intl.NumberFormat("en-AU", {
                                                            style: "currency",
                                                            currency: "AUD"
                                                        }).format(stageData?.totalUnsettledContractPrice) : undefined
                                                    }
                                                </GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>
                                                    Average Unsettled Contract Price: 
                                                </GridLabel>
                                                <GridData>
                                                    {
                                                        stageData?.averageUnsettledContractPrice ? Intl.NumberFormat("en-AU", {
                                                            style: "currency",
                                                            currency: "AUD"
                                                        }).format(stageData?.averageUnsettledContractPrice) : undefined
                                                    }
                                                </GridData>
                                            </GridRow>
                                            <GridRow>
                                                <GridLabel>
                                                    Total Exchanged Contract Price: 
                                                </GridLabel>
                                                <GridData>
                                                    {
                                                        stageData?.totalExchangedContractPrice ? Intl.NumberFormat("en-AU", {
                                                            style: "currency",
                                                            currency: "AUD"
                                                        }).format(stageData?.totalExchangedContractPrice) : undefined
                                                    }
                                                </GridData>
                                            </GridRow>
                                            <GridFillerRow />
                                        </GridColumn>
                                    </DetailsContent>
                                </DetailsPanel>
                            </Grid>
                        </Grid>
                        <StyledPaper>
                            <Box
                                sx={{
                                    marginTop: theme.spacing(2),
                                    [`& .${gridClasses.root}`]: {
                                        [`& .${gridClasses.columnHeader}`]: {
                                            height: 'auto !important',
                                            minHeight: 56,
                                        
                                            [`& .${gridClasses.columnHeaderTitleContainer}`]: {
                                                whiteSpace: 'normal',
                                        
                                                [`& .${gridClasses.columnHeaderTitle}`]: {
                                                    whiteSpace: 'normal',
                                                    paddingTop: theme.spacing(1.5),
                                                    paddingBottom: theme.spacing(1.5) 
                                                }
                                            },
                                        
                                            [`& .${gridClasses.iconButtonContainer}`]: {
                                                width: 'unset',
                                            },

                                            '&.column-group-header': {
                                                backgroundColor: theme.palette.grey[300],
                                                boxShadow: "1",
                                                textAlign: 'center'
                                            }
                                        }
                                    },
                                }}
                            >
                                <CollapsibleTablePremium
                                    density="compact"
                                    startCollapsed={false} 
                                    title={"Lots"} 
                                    loading={lotsLoading} 
                                    columns={columns}
                                    rows={lotSummaries}
                                    disableColumnMenu={false}
                                    disableColumnFilter={false}
                                    pagination
                                    aggregationModel={aggregationModel}
                                    {...customToolbarProps}
                                />
                            </Box>
                        </StyledPaper>
                        <StageAttachments stageId={stageId} />
                    </StageAttachmentsContext.Provider>
                </LotSummariesContext.Provider>
            </StageDetailsContext.Provider>
        </>
    );
}